export const privacyPolicyMarkdown = `
# Privacy Policy

**Effective as of:** {{EFFECTIVE_DATE}}  
**Website:** {{WEBSITE_URL}}  
**Contact:** {{SUPPORT_EMAIL}}

## IMPORTANT PRIVACY INFORMATION

When you access our Website, we may automatically collect language settings, IP address, time zone, device type/model, device settings, operating system, internet provider, mobile carrier, hardware ID, advertising identifiers, and cookies.

We use third-party tools and vendors to provide the Service, analytics, communications, ads, and payments, including: Amazon, Amplitude, Appsflyer, BigQuery, Crashlytics, Firebase, Google, Hotjar, Iterable, Meta, OpenAI, PayPal, Solidgate, Tableau, TikTok, Zendesk.

By using the Service, you confirm that:
1. You have read and agreed to this Privacy Policy.
2. You are at least 16 years old (or your parent/legal guardian has agreed on your behalf).

If you do not agree, do not use the Service and contact us at {{SUPPORT_EMAIL}}.

---

## 1. Personal Data Controller

The data controller is: **{{COMPANY_LEGAL_NAME}}**, contact email: **{{SUPPORT_EMAIL}}**.

---

## 2. Categories of Personal Data We Collect

### 2.1 Data you provide
- Name
- Email
- Account details
- Input/files/feedback submitted to the Service
- Payment-related records required for order processing

### 2.2 Data collected automatically
- Referral source (where you came from)
- Device and technical data (IP, OS, browser, language, timezone, identifiers)
- Usage data (pages viewed, interactions, session behavior, purchases)
- Advertising identifiers (IDFA/AAID/IDFV where applicable)
- Transaction metadata (date/time/amount/payment method type)
- Cookies and similar technologies

---

## 3. Purposes of Processing

We process personal data to:

1. Provide and operate the Service
2. Generate requested outputs
3. Deliver customer support
4. Communicate service-related notices
5. Analyze and improve the Service
6. Send marketing communications
7. Personalize and measure advertising
8. Process payments
9. Enforce Terms and prevent fraud
10. Comply with legal obligations

Third-party processors may include:
- Hosting/infrastructure: Amazon Web Services, Google Cloud
- AI services: OpenAI
- Analytics: Amplitude, Appsflyer, Firebase, BigQuery, Tableau, Hotjar, Meta
- Communications/support: Zendesk, Firebase, Iterable
- Advertising: Meta, Google, TikTok
- Payments: Solidgate, PayPal

---

## 4. Legal Bases (EEA Users)

For EEA users, processing is based on:
1. Performance of a contract
2. Legitimate interests (service improvement, communications, fraud prevention, marketing, ad personalization)
3. Compliance with legal obligations

---

## 5. Data Sharing

We may share data with:

### 5.1. Service providers

We share personal data with third parties that we hire to provide services or perform business functions on our behalf, based on our instructions. We may share your personal information with the following types of service providers:
- artificial intelligence services providers (Open.AI)
- cloud storage providers (Amazon Web Services, Google)
- data analytics providers (Appsflyer, Amplitude, Google, Meta, Tableau, Hotjar, Firebase, BigQuery)
- communication service providers (Zendesk, Firebase, Iterable)
- marketing partners (in particular, social media networks, marketing agencies, email delivery services; Meta, Google, TikTok)
- payment service providers (PayPal, Solid)
- infrastructure monitoring services (Firebase)

### 5.2. Law enforcement agencies and other public authorities

We may use and disclose personal data to enforce our [Terms and Conditions of Use](/legal/terms-of-use), to protect our rights, privacy, safety, or property, and/or that of our affiliates, you or others, and to respond to requests from courts, law enforcement agencies, regulatory agencies, and other public and government authorities, or in other cases provided for by law.

### 5.3. Third parties as part of a merger or acquisition

As we develop our business, we may buy or sell assets or business offerings. Customers' information is generally one of the transferred business assets in these types of transactions. We may also share such information with any affiliated entity (e.g. parent company or subsidiary) and may transfer such information in the course of a corporate transaction, such as the sale of our business, a divestiture, merger, consolidation, or asset sale, or in the unlikely event of bankruptcy.

---

## 6. Your Privacy Rights

Depending on jurisdiction, you may have rights to:
- Access, review, correct your data
- Delete your data
- Restrict or object to processing
- Data portability
- Lodge a complaint with a supervisory authority (EEA)

To exercise rights, contact: **{{SUPPORT_EMAIL}}**.

---

## 7. Age Limitation

We do not knowingly process personal data of users under 16.

---

## 8. International Data Transfers

Your data may be transferred across jurisdictions to provide the Service.  
Where required, we use safeguards (e.g., SCCs, adequacy decisions).

---

## 9. Changes to This Privacy Policy

We may update this Policy from time to time. Material changes will be communicated via appropriate channels (e.g., email or in-product notice).

---

## 10. California Privacy Rights

California residents may have rights to:
- Know what personal information is collected/used/shared
- Request deletion
- Opt out where applicable
- Non-discrimination for exercising rights

Requests: **{{SUPPORT_EMAIL}}**.

---

## 11. Data Retention

We retain data as long as reasonably necessary for Service purposes, legal compliance, dispute resolution, and enforcement.

---

## 12. “Do Not Track”

Except where explicitly stated, the Website does not respond to “Do Not Track” signals.

---

## 13. Contact Us

For any privacy questions or requests: **{{SUPPORT_EMAIL}}**.
`;

export const subscriptionTermsMarkdown = `
# Subscription Terms

**Last Updated:** {{LAST_UPDATED}}  
**Service:** {{SITE_NAME}}  
**Contact:** {{SUPPORT_EMAIL}}

## 1. Trial

We may offer free or paid trial access.  
Unless canceled at least 24 hours before trial end, trial may auto-convert to a paid subscription at the price and period shown at checkout.

## 2. Subscription

Subscriptions renew automatically at the end of each billing cycle (weekly/monthly/6 months/year/other selected period) until canceled.

## 3. Payment Method

Charges are billed to the payment method provided at purchase.  
You authorize us to charge applicable subscription fees.

## 4. Cancellation

Canceling disables future auto-renewal, while access remains until the current paid period ends.  
You can cancel via account settings or by contacting support: **{{SUPPORT_EMAIL}}**.

If cancellation is linked to a personal-data-erasure request, access may terminate immediately after such request is completed.

## 5. Changes

To the extent permitted by law, we may change subscription fees at any time with reasonable notice (in-app, email, or other prominent notice).  
If you do not accept new fees, cancel before they take effect.

## 6. Refund Rules

### 6.1 General
Purchases are generally non-refundable/non-exchangeable unless required by law or explicitly stated otherwise.

### 6.2 EU Users
EU users may withdraw within 14 days without reason.  
To withdraw, contact us by email at **{{SUPPORT_EMAIL}}** (withdrawal form may be offered but is not mandatory).  
If withdrawal is valid, reimbursement is made within 14 days via original payment method (unless otherwise agreed).

If you expressly consented to immediate digital-service performance and acknowledged withdrawal-right loss, refunds may be limited as required by applicable law.

---

If you have questions about these Subscription Terms, contact: **{{SUPPORT_EMAIL}}**.
`;

export const termsOfUseMarkdown = `
# Terms and Conditions of Use

**Last Updated:** {{LAST_UPDATED}}  
**Website:** {{WEBSITE_URL}}  
**Contact:** {{SUPPORT_EMAIL}}

> The Service may offer subscriptions that automatically renew.  
> To avoid charges, cancel trial/subscription at least 24 hours before renewal.

---

## 1. Acceptance of Terms

1. These Terms govern your use of {{WEBSITE_URL}} and related services/content, provided by {{COMPANY_LEGAL_NAME}}, a company incorporated under the laws of the Republic of Cyprus, having its registered office at {{COMPANY_LEGAL_ADDRESS}}.
2. By using the Service, you agree to be legally bound by these Terms.
3. Privacy Policy is incorporated by reference.
4. English version controls in case of translation conflicts.
5. We may update Terms; continued use means acceptance.
6. Terms include important disclaimers, limitation of liability, arbitration, and class action waiver.
7. If you do not agree, do not use the Service.

---

## 2. Important Disclaimers

We do not guarantee uninterrupted, error-free, fully secure Service, or that outcomes will meet your expectations.

---

## 3. Account Registration

- You may need an account for certain features.
- You must provide accurate/current information.
- Minimum age is 16 (or parental/guardian consent where required).
- We may suspend/terminate for violations.
- You are responsible for account credentials and activity.

---

## 4. Service Conditions

- Service may be modified/suspended at any time.
- You are responsible for required equipment/internet access.
- Access/use is at your own risk.
- Support may be provided at our discretion.

---

## 5. Intellectual Property and User Content

### Ownership and Intellectual Property

The Service, including its software, content, logos, trademarks, and any associated materials, remains the exclusive property of the Company or its licensors. Accessing or using the Service does not grant you ownership of any intellectual property rights beyond what is explicitly stated in these Terms. You may not copy, modify, distribute, sell, or reverse-engineer any portion of the Service unless expressly permitted.

### License to Use the Service

You are granted a limited, non-exclusive, non-transferable, revocable license to access and use the Service for personal, non-commercial purposes. Any breach of these Terms may result in the immediate suspension or termination of your access to the Service.

### User-Generated Content

You may submit, upload, transmit, or otherwise make available to the Service prompts, queries, text, images, audio, video, files, feedback, and other materials as inputs ("Inputs"), and the Service may generate content in response ("Outputs"). Inputs and Outputs, together with any other materials you submit through the Service, are collectively referred to as "User Content." As between you and the Company, you retain ownership of your User Content to the extent you hold pre-existing rights in it.

By submitting User Content, you hereby grant the Company and its affiliates, sublicensees, successors, and assigns a royalty-free, fully paid-up, perpetual, irrevocable, sublicensable, transferable, and worldwide license to use, reproduce, store, modify, adapt, translate, create derivative works from, distribute, publicly perform, publicly display, and otherwise exploit your User Content, in whole or in part, in any form, media, or technology now known or hereafter developed, for any purpose, including to: (i) operate, maintain, and improve the Service; (ii) develop, train, fine-tune, and evaluate artificial intelligence and machine learning models; (iii) conduct research and analytics; (iv) ensure safety and trust across the platform; and (v) create, market, and distribute other products and services. You further grant the Company the right to sublicense User Content to third-party service providers and partners to the extent necessary to operate the Service. The Company shall have no obligation to maintain the confidentiality of any User Content, except as expressly set forth in the [Privacy Policy](/legal/privacy-policy). To the fullest extent permitted by applicable law, you irrevocably waive, and agree not to assert, any moral rights or similar rights you may have in your User Content.

Notwithstanding the foregoing, this license does not apply to personal data as defined under applicable privacy legislation, which shall be processed in accordance with the Company's [Privacy Policy](/legal/privacy-policy).

### User Reviews and Testimonials

By submitting, posting, or otherwise providing any review, rating, comment, testimonial, or other feedback ("Review") about the Service on any platform, including but not limited to the Apple App Store, Google Play Store, and other mobile application marketplaces, websites, social media platforms, or directly to the Company, you grant the Company and its affiliates a non-exclusive, worldwide, perpetual, irrevocable, royalty-free, sublicensable, and transferable right to use, reproduce, modify, adapt, publish, translate, distribute, publicly perform, publicly display, and create derivative works from such Reviews for any lawful purpose, including but not limited to marketing, advertising, promotional activities, product development, and customer engagement, in any media now known or later developed, without further notice, attribution, or compensation to you.

You acknowledge and agree that:

- The Company is not obligated to use, display, or maintain any Review and may remove or edit Reviews at its discretion.
- The Company may use Reviews in conjunction with your publicly displayed username, profile picture, or other identifying information (if available), unless you request anonymity in writing.
- The Company is not responsible for the content of Reviews posted by users and does not endorse any opinions expressed therein.

If you wish to request the removal of a Review that you have submitted, you may contact the Company at {{SUPPORT_EMAIL}}.

### User Responsibilities and Rights

You retain ownership of any User Content you submit, subject to the license granted to the Company. By submitting User Content, you represent and warrant that you own or have all necessary rights and permissions to grant the above license. You confirm that your content does not infringe upon any third-party intellectual property, privacy, or contractual rights and that any third-party rights, including moral rights, in the User Content have been lawfully waived or granted to you. The Company and its successors may use the User Content without compensating you.

Once submitted, User Content may remain accessible even if removed by you. If you wish to revoke the granted license for specific User Content, you may contact {{SUPPORT_EMAIL}}. However, any prior use of the content will not be affected. The Company is not responsible for storing or maintaining copies of removed User Content and is not liable for any loss incurred due to its removal.

### Content Moderation and Restrictions

The Company does not actively monitor User Content but reserves the right to review, edit, remove, or restrict content at its discretion. This applies particularly to content that contains offensive, illegal, defamatory, or misleading material, violates third-party intellectual property, privacy, or contractual rights, promotes harm, violence, harassment, or illegal activity, or disrupts the functionality, security, or reputation of the Service. The Company assumes no liability for the accuracy or legality of User Content submitted by others.

### Prohibited Conduct

You agree not to use the Service to distribute illegal, deceptive, or harmful content, impersonate another individual or misrepresent your affiliation, reverse-engineer, extract, or manipulate any part of the Service, or interfere with the security, availability, or integrity of the Service. Violation of these Terms may result in the immediate suspension or termination of your account, as well as legal consequences.

### Service Availability and Modifications

The Company reserves the right to modify, suspend, or discontinue any aspect of the Service at any time without liability. Certain features may not be available in all regions or on all devices. If a modification impacts your use of the Service, you may cancel your subscription or delete your account.

Also please make sure you are using the latest version of your web browser when accessing the Website, as that will help prevent security problems and ensure all the Website features work for you.

### Risks and Disclaimer of Liability

Your use of the Service is at your own risk. The Company does not guarantee the accuracy, reliability, or fitness of any content provided. We are not responsible for loss of data, device malfunctions, or technical failures, any reliance on fitness, nutrition, or wellness recommendations within the Service, or personal injury, financial loss, or any legal claims arising from your use of the Service.

### Customer Support

Customer support services are provided at the Company's discretion. While we may assist users, there is no obligation to provide support or respond to inquiries. If you require assistance, contact {{SUPPORT_EMAIL}}, and we will respond as reasonably possible.

---

## 6. Payments and Refunds

1. Certain features are paid.
2. Prices may change with notice.
3. You authorize charges to provided payment method.
4. Subscriptions may auto-renew until canceled.
5. Trials may auto-convert unless canceled in time.
6. To the extent allowed by law, purchases are generally non-refundable unless required by law.

### EU Right of Withdrawal
EU users may withdraw within 14 days, unless digital-content exceptions apply after explicit prior consent to immediate performance and acknowledgment of withdrawal-right loss.

---

## 7. User Representations and Restrictions

By using the Service, you represent that:
- You have legal capacity and comply with applicable laws.
- You are at least 16.
- You do not use bots/scrapers/automated abusive tools.
- You do not misuse, disrupt, or circumvent security of the Service.
- You do not upload harmful, illegal, infringing, hateful, explicit, deceptive, or privacy-violating content.

We may suspend/terminate access for violations.

---

## 8. Disclaimer of Warranties

### General Disclaimers

EXCEPT TO THE EXTENT PROHIBITED BY LAW OR OTHERWISE INAPPLICABLE, YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT YOUR USE OF THE SERVICE IS AT YOUR OWN RISK. THE SERVICE AND ANY PRODUCTS OR CONTENT PROVIDED THROUGH IT ARE MADE AVAILABLE **"AS IS"** AND **"AS AVAILABLE,"** WITHOUT ANY WARRANTIES OR GUARANTEES OF ANY KIND, EXPRESS OR IMPLIED.

TO THE FULLEST EXTENT PERMITTED BY LAW, THE COMPANY AND ITS AFFILIATES, OFFICERS, EMPLOYEES, AGENTS, PARTNERS, AND LICENSORS EXPRESSLY DISCLAIM ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, ACCURACY, AND RELIABILITY OF CONTENT OR DATA.

IN PARTICULAR, WE DO NOT WARRANT THAT:
- The Service will meet your expectations or requirements;
- The Service will be uninterrupted, secure, error-free, or free from technical issues;
- The results obtained from using the Service will be accurate, reliable, or error-free;
- The quality of any content, features, or services will meet your expectations;
- Any defects or errors will be corrected promptly or at all.

ANY MATERIAL, DATA, OR INFORMATION OBTAINED THROUGH THE SERVICE IS ACCESSED AT YOUR OWN DISCRETION AND RISK. YOU ARE SOLELY RESPONSIBLE FOR ANY DAMAGE TO YOUR DEVICE OR LOSS OF DATA RESULTING FROM YOUR USE OF THE SERVICE.

WE DO NOT GUARANTEE OR PROMISE ANY SPECIFIC RESULTS FROM USING THE SERVICE. BY USING THE SERVICE, YOU ACCEPT THE INHERENT RISKS OF SERVICE INTERRUPTIONS, TECHNICAL FAILURES, AND POTENTIAL DATA LOSS.

### No Professional or Medical Advice

ANY INFORMATION OR STATEMENTS AVAILABLE THROUGH THE SERVICE ARE FOR INFORMATIONAL AND GENERAL PURPOSES ONLY. THEY ARE NOT INTENDED TO REPLACE OR SUBSTITUTE FOR PROFESSIONAL FINANCIAL, MEDICAL, LEGAL, OR OTHER SPECIALIZED ADVICE.

THE COMPANY DOES NOT REPRESENT OR WARRANT THAT ANY INFORMATION PROVIDED THROUGH THE SERVICE IS RELIABLE, COMPLETE, OR SUITABLE FOR YOUR SPECIFIC NEEDS. YOU ACKNOWLEDGE THAT ANY DECISIONS MADE BASED ON INFORMATION PROVIDED THROUGH THE SERVICE ARE YOUR SOLE RESPONSIBILITY, AND IF YOU REQUIRE PROFESSIONAL ADVICE, YOU SHOULD CONSULT A QUALIFIED SPECIALIST.

TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE COMPANY EXPRESSLY DISCLAIMS ANY LIABILITY FOR YOUR RELIANCE ON ANY STATEMENTS, RECOMMENDATIONS, OR CONTENT PROVIDED THROUGH THE SERVICE.

### Service Modifications and Updates

We reserve the right to update, modify, or discontinue any aspect of the Service, including features, content, and availability, at any time, with or without notice. This includes changes to:
- The information provided on our website and mobile applications;
- The availability or functionality of any Service feature;
- The pricing, structure, or terms of use of the Service.

We are not responsible for any loss or inconvenience resulting from modifications, suspensions, or discontinuations of any part of the Service.

### Consumer Protection and Non-Waivable Rights

Nothing in these Terms shall exclude or limit any consumer rights that cannot be waived under applicable law. If you are entitled to statutory rights under the laws of your country of residence, those rights remain unaffected by these disclaimers.

---

## 9. Limitation of Liability

To the fullest extent permitted by law:
- We are not liable for indirect, incidental, consequential, special, exemplary, or punitive damages.
- Aggregate liability is limited to amounts paid by you to us for the Service.

---

## 10. Indemnification

You agree to indemnify and hold harmless the Company and related parties from claims/losses arising from your use of the Service, your User Content, or your breach of these Terms.

---

## 11. International Use

You are responsible for compliance with local laws in your jurisdiction.

---

## 12. Mandatory Arbitration and Class Action Waiver

1. Most disputes are resolved by final and binding arbitration.
2. You waive jury trial and class/representative action rights, where lawful.
3. Arbitration is administered by AAA under applicable rules.
4. Arbitration notice address (default): **Florinis, 7, Greg Tower, 2nd floor, 1065, Nicosia, Cyprus**.
5. Opt-out right: you may opt out of arbitration within 30 days of first use by written notice to {{SUPPORT_EMAIL}} (or arbitration notice address), with sufficient identifying details.

---

## 13. Governing Law

- Non-US users: laws of Cyprus (excluding conflict-of-laws principles), courts of Nicosia where court venue applies.
- US users: laws of the United States/Commonwealth of Virginia (as applicable in source terms), courts in Alexandria, Virginia where court venue applies.

---

## 14. Miscellaneous

- No waiver by delay.
- If one provision is unenforceable, remaining provisions survive.
- Terms are the entire agreement on subject matter.
- We may assign rights/obligations.
- Electronic notices/signatures are valid.
- Force majeure applies to events beyond reasonable control.

---

## 15. Contact

For legal notices/questions: **{{SUPPORT_EMAIL}}**.

---

**I HAVE READ THESE TERMS AND AGREE TO ALL OF THE PROVISIONS CONTAINED ABOVE.**
`;
